// src/lib/oneOnOne.js
// STATUS: active | UPDATED: 2026-09-27
// LOG:
//   2026-09-27  getLatestForMe, for the salesperson's agreement card on Today.
//   2026-09-23  New. See migration 0039 for why this table has the columns it
//               has. Shape and date helpers live in oneOnOneShape.js so they
//               can be tested.
//
// Jazz's one on one, as a record that comes back filled in.

import { supabase } from './supabase'
import { dayKey } from './oneOnOneShape'

export * from './oneOnOneShape'

const COLS = `
  id, dealership_id, manager_id, salesperson_id, held_on,
  pipeline, unsold, activity, obstacles,
  behavior, impact, correction, repetition,
  inspect_on, inspected_at, inspection_note, updated_at
`

// Who a manager can run one of these with. Employees on their own floor, which
// is what the RLS on `users` already scopes for them, so this does not re-state
// the rule.
export async function listFloor(dealershipId) {
  if (!dealershipId) return { data: [], error: null }
  const { data, error } = await supabase
    .from('users')
    .select('id, display_name, first_name, last_name, initials, role')
    .eq('dealership_id', dealershipId)
    .eq('role', 'employee')
    .order('display_name')
  return { data: data || [], error }
}

export async function listRecent(dealershipId, limit = 25) {
  if (!dealershipId) return { data: [], error: null }
  const { data, error } = await supabase
    .from('one_on_ones')
    .select(COLS)
    .eq('dealership_id', dealershipId)
    .order('held_on', { ascending: false })
    .limit(limit)
  return { data: data || [], error }
}

// A salesperson's own latest one on one, for the agreement card on Today.
// one_on_ones_own_select lets them read it (0039). Only the columns the card
// uses, so the critique never travels to the front door at all.
export async function getLatestForMe(userId) {
  if (!userId) return { data: null, error: null }
  const { data, error } = await supabase
    .from('one_on_ones')
    .select('held_on, manager_id, correction, inspect_on, inspected_at')
    .eq('salesperson_id', userId)
    .order('held_on', { ascending: false })
    .limit(1)
    .maybeSingle()
  return { data: error ? null : data || null, error }
}

// The query the screen exists for. A dated promise to look again, that nobody
// has looked at. Ordered oldest first, because the one that has been waiting
// longest is the one that has stopped meaning anything.
export async function listDue(dealershipId, today = dayKey()) {
  if (!dealershipId) return { data: [], error: null }
  const { data, error } = await supabase
    .from('one_on_ones')
    .select(COLS)
    .eq('dealership_id', dealershipId)
    .is('inspected_at', null)
    .not('inspect_on', 'is', null)
    .lte('inspect_on', today)
    .order('inspect_on')
  return { data: data || [], error }
}

export async function getFor(salespersonId, heldOn) {
  if (!salespersonId) return { data: null, error: null }
  const { data, error } = await supabase
    .from('one_on_ones')
    .select(COLS)
    .eq('salesperson_id', salespersonId)
    .eq('held_on', heldOn)
    .maybeSingle()
  return { data: data || null, error }
}

// UPSERT ON (salesperson_id, held_on), which 0039 made unique. A manager who
// saves twice in one meeting is correcting the first save, not recording a
// second meeting.
//
// Empty strings are stored as null rather than as '', so "not discussed" and
// "discussed, nothing to say" do not look identical to every query later.
export async function saveOneOnOne({ dealershipId, managerId, salespersonId, heldOn, fields }) {
  if (!dealershipId) return { error: 'No dealership on your account.' }
  if (!salespersonId) return { error: 'Pick who this was with.' }

  const row = {
    dealership_id: dealershipId,
    manager_id: managerId,
    salesperson_id: salespersonId,
    held_on: heldOn || dayKey(),
  }
  for (const [k, v] of Object.entries(fields || {})) {
    const text = typeof v === 'string' ? v.trim() : v
    row[k] = text === '' || text == null ? null : text
  }

  const { data, error } = await supabase
    .from('one_on_ones')
    .upsert(row, { onConflict: 'salesperson_id,held_on' })
    .select(COLS)
    .single()
  return { data, error: error ? error.message || String(error) : null }
}

// Closing the loop. The note is optional and the timestamp is not: what matters
// is that somebody went back and looked, and when.
export async function markInspected(id, note) {
  const { data, error } = await supabase
    .from('one_on_ones')
    .update({
      inspected_at: new Date().toISOString(),
      inspection_note: String(note || '').trim() || null,
    })
    .eq('id', id)
    .select(COLS)
    .single()
  return { data, error: error ? error.message || String(error) : null }
}
